import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  email: string;
  name: string;
  picture: string;
}

export type AccessRole = 'admin' | 'hr' | 'user';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** From GET /api/auth/verify — admin, HR, or legacy user role may edit person contact. */
  canEditContacts: boolean;
  /** Admin or HR (not legacy `user`) — LinkedIn on internal employee profiles. */
  canEditEmployeeLinkedIn: boolean;
  accessRole: AccessRole | null;
  login: (token: string, user: User, isAdmin?: boolean) => void;
  setAdmin: (isAdmin: boolean) => void;
  setSessionAccess: (p: {
    isAdmin: boolean;
    canEditContacts: boolean;
    canEditEmployeeLinkedIn: boolean;
    accessRole: AccessRole | null;
  }) => void;
  logout: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isAdmin: false,
      canEditContacts: false,
      canEditEmployeeLinkedIn: false,
      accessRole: null,
      login: (token: string, user: User, isAdmin?: boolean) => {
        set({
          token,
          user,
          isAuthenticated: true,
          isAdmin: isAdmin ?? false,
          canEditContacts: false,
          canEditEmployeeLinkedIn: false,
          accessRole: null,
        });
      },
      setAdmin: (isAdmin: boolean) => {
        set({ isAdmin });
      },
      setSessionAccess: (p: {
        isAdmin: boolean;
        canEditContacts: boolean;
        canEditEmployeeLinkedIn: boolean;
        accessRole: AccessRole | null;
      }) => {
        set({
          isAdmin: p.isAdmin,
          canEditContacts: p.canEditContacts,
          canEditEmployeeLinkedIn: p.canEditEmployeeLinkedIn,
          accessRole: p.accessRole,
        });
      },
      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isAdmin: false,
          canEditContacts: false,
          canEditEmployeeLinkedIn: false,
          accessRole: null,
        });
      },
    }),
    {
      name: 'cd-people-auth', // localStorage key
    }
  )
);

/**
 * Call when a response is received. If status is 401, clears session (logout) so the app
 * shows login and avoids broken partial UI (e.g. expired token with admin/avatar failing).
 * Use after any fetch() that sends the auth token.
 * @returns true if response was 401 and session was cleared
 */
export function clearSessionIf401(response: Response): boolean {
  if (response.status === 401) {
    useAuth.getState().logout();
    return true;
  }
  return false;
}
